import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError } from '../../../lib/api'
import { requestPasswordResetToken } from '../api'
import { AuthAlert } from '../components/AuthAlert'
import { AuthFormIntro } from '../components/AuthFormIntro'
import { AuthSplitShell } from '../components/AuthSplitShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

type FeedbackType = 'success' | 'error' | 'warning' | null

export function PasswordForgotPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(null)

  const forgotMutation = useMutation({
    mutationFn: requestPasswordResetToken,
    onSuccess: () => {
      setFeedbackType('success')
      setFeedback(t('system.passwordForgot.successMessage'))
    },
    onError: (error) => {
      setFeedbackType('error')
      const apiError = error as ApiError
      if (apiError.errors?.email?.[0]) {
        setFeedback(t('auth.errors.emailNotFound'))
      } else {
        setFeedback(apiError.message)
      }
    },
  })

  const handleSubmit = () => {
    setFeedback(null)
    setFeedbackType(null)

    const trimmed = email.trim()
    if (!trimmed) {
      setFeedbackType('warning')
      setFeedback(t('auth.errors.emailRequired'))
      return
    }

    forgotMutation.mutate({ email: trimmed })
  }

  return (
    <AuthSplitShell>
      <div className="w-full space-y-5">
        <AuthFormIntro
          title={t('system.passwordForgot.title')}
          description={t('system.passwordForgot.description')}
        />

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="password-forgot-email">
              {t('auth.email')}
            </label>
            <Input
              id="password-forgot-email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                if (feedback) {
                  setFeedback(null)
                  setFeedbackType(null)
                }
              }}
              type="email"
              placeholder={t('system.passwordForgot.emailPlaceholder')}
              className="px-4"
            />
          </div>

          {feedback && feedbackType ? (
            <AuthAlert variant={feedbackType}>
              {feedback}
            </AuthAlert>
          ) : null}

          <Button
            size="lg"
            type="button"
            onClick={handleSubmit}
            disabled={forgotMutation.isPending}
            className="w-full"
          >
            {forgotMutation.isPending
              ? t('system.passwordForgot.requesting')
              : t('system.passwordForgot.submit')}
          </Button>
        </div>

        <div className="flex justify-center pt-2">
          <Link
            to="/login"
            className="text-sm font-medium text-foreground underline underline-offset-4 transition hover:text-foreground/80"
          >
            {t('system.passwordForgot.backToLogin')}
          </Link>
        </div>
      </div>
    </AuthSplitShell>
  )
}
